import React from 'react';

/** Reusable colour swatch input. */
export function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase text-slate-400">{value}</span>
        <input
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
          className="h-7 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
        />
      </div>
    </div>
  );
}

/** Reusable labelled slider with a live numeric readout. */
export function SliderField({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = '',
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block py-1.5">
      <span className="flex items-center justify-between text-xs text-slate-600">
        {label}
        <span className="text-[11px] font-medium text-slate-500">
          {value}
          {suffix}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        // Named explicitly: the wrapping label also contains the live value
        // readout, which would otherwise be read out as part of the name.
        aria-label={label}
        className="mt-1 w-full accent-blue-600"
      />
    </label>
  );
}

/**
 * Section wrapper used by every properties panel for a consistent rhythm.
 *
 * Optionally collapsible: a tall panel (labeling has six sections) is far
 * easier to work with when the parts you are not editing fold away.
 */
export function PropertySection({
  title,
  children,
  collapsible = false,
  defaultOpen = true,
  trailing,
}: {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Small control rendered on the header row, e.g. a toggle. */
  trailing?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const expanded = collapsible ? open : true;

  return (
    <section className="border-t border-slate-100 px-4 py-3 first:border-t-0">
      <div className="mb-1 flex items-center justify-between gap-2">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={expanded}
            className="flex flex-1 items-center gap-1 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 transition hover:text-slate-700"
          >
            <span
              aria-hidden="true"
              className="inline-block text-[9px] transition-transform"
              style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
            >
              ▶
            </span>
            {title}
          </button>
        ) : (
          <h4 className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {title}
          </h4>
        )}
        {trailing}
      </div>
      {expanded && children}
    </section>
  );
}

/** Reusable on/off switch used by the labeling panel's boolean options. */
export function ToggleField({
  label,
  checked,
  onChange,
  description,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 py-1.5">
      <span className="min-w-0">
        <span className="block text-xs text-slate-600">{label}</span>
        {description && (
          <span className="block text-[10px] leading-snug text-slate-400">{description}</span>
        )}
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 flex-shrink-0 cursor-pointer accent-blue-600"
      />
    </label>
  );
}
